import React, { useState, useCallback, useContext, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Typography, IconButton, SpeedDial, SpeedDialIcon, SpeedDialAction, Drawer, Box } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AppContext from "~/presentation/AppContext";
import { makeExplanatoryVideoCategoryUseCases } from "~/main/factories/use-cases/explanatory-video-category";
import { brandColors, designTokens } from "~/presentation/themes/modernTheme";
import HelpCenterContent from "./HelpCenterContent";

const FAB_SIZE = 52;
const FAB_BOTTOM = 24;
const FAB_RIGHT = 24;
const FAB_Z_INDEX = 1299;
const DRAWER_WIDTH = 560;
const AUTO_REVEAL_DURATION_MS = 3000;

const PHONE = "557182836848";
const MESSAGE = "Descreva a sua solicitação de atendimento";

function digitsOnly(str = "") {
  return String(str).replace(/[^\d]/g, "");
}

const whatsappHref = `https://wa.me/${digitsOnly(PHONE)}?text=${encodeURIComponent(MESSAGE)}`;

const speedDialSx = {
  position: "fixed",
  bottom: FAB_BOTTOM,
  right: FAB_RIGHT,
  zIndex: FAB_Z_INDEX,
  "& .MuiSpeedDial-fab": {
    width: FAB_SIZE,
    height: FAB_SIZE,
    backgroundColor: brandColors.primary[500],
    color: "#ffffff",
    boxShadow: "0 6px 16px rgba(0,0,0,.25)",
    transition: "transform .15s ease, box-shadow .15s ease",
    "&:hover": {
      backgroundColor: brandColors.primary[800],
      transform: "translateY(-1px)",
      boxShadow: "0 10px 24px rgba(0,0,0,.3)",
    },
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      borderRadius: "9999px",
      boxShadow: "0 0 0 0 rgba(79, 70, 229, 0.45)",
      pointerEvents: "none",
    },
    "&::before": { animation: "gt-wa-pulse 2s ease-out infinite" },
    "&::after":  { animation: "gt-wa-pulse 2s ease-out infinite .6s" },
  },
};

const drawerSx = {
  zIndex: 1300,
  "& .MuiDrawer-paper": {
    width: DRAWER_WIDTH,
    maxWidth: "100vw",
    borderLeft: `1px solid ${brandColors.neutral[200]}`,
    boxShadow: designTokens.shadows.xl,
  },
};

const drawerHeaderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  px: `${designTokens.spacing.lg}px`,
  py: `${designTokens.spacing.md}px`,
  borderBottom: `1px solid ${brandColors.neutral[200]}`,
  minHeight: 64,
};

const drawerBodySx = {
  flex: 1,
  overflowY: "auto",
  p: `${designTokens.spacing.lg}px`,
};

function normalizePathname(pathname) {
  const trimmed = pathname.replace(/\/+$/, "");
  return trimmed || "/";
}

export default function HelpCenter() {
  const { isLogged } = useContext(AppContext);
  const { pathname } = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);

  const lastFetchedPathRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const openTimer = setTimeout(() => setSpeedDialOpen(true), 300);
    const closeTimer = setTimeout(() => setSpeedDialOpen(false), AUTO_REVEAL_DURATION_MS);
    return () => {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
    };
  }, [pathname]);

  const fetchHelpContent = useCallback(async () => {
    const normalizedPath = normalizePathname(pathname);

    if (lastFetchedPathRef.current === normalizedPath) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    setIsLoading(true);
    setError(null);

    try {
      const useCases = makeExplanatoryVideoCategoryUseCases();
      const data = await useCases.getCategoriesByRoute(normalizedPath);

      if (signal.aborted) return;

      setCategories(data && data.length > 0 ? data : null);
      lastFetchedPathRef.current = normalizedPath;
    } catch (fetchError) {
      if (signal.aborted) return;
      setError(fetchError);
      setCategories(null);
    } finally {
      if (!signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [pathname]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    fetchHelpContent();
  }, [fetchHelpContent]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const actions = [
    { 
      icon: <WhatsAppIcon sx={{ color: '#25D366' }} />, 
      name: 'Suporte via WhatsApp', 
      onClick: () => window.open(whatsappHref, '_blank', 'noopener,noreferrer')
    },
    ...(isLogged() ? [{ 
      icon: <HelpOutlineIcon sx={{ color: brandColors.primary[500] }} />, 
      name: 'Ajuda e Vídeos', 
      onClick: () => handleOpen() 
    }] : []),
  ];

  return (
    <>
      <style>{`
        @keyframes gt-wa-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(79, 70, 229, 0.45); }
          70%  { box-shadow: 0 0 0 16px rgba(79, 70, 229, 0); }
          100% { box-shadow: 0 0 0 0 rgba(79, 70, 229, 0); }
        }
       
      `}</style>
      
      {!isOpen && (
        <SpeedDial
          ariaLabel="Central de Atendimento e Ajuda"
          sx={speedDialSx}
          icon={<SpeedDialIcon icon={<HeadsetMicIcon />} openIcon={<CloseIcon />} />}
          open={speedDialOpen}
          onOpen={() => setSpeedDialOpen(true)}
          onClose={() => setSpeedDialOpen(false)}
        >
          {actions.map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              tooltipOpen
              onClick={action.onClick}
              sx={{
                "& .MuiSpeedDialAction-staticTooltipLabel": {
                  whiteSpace: "nowrap",
                  fontWeight: 500,
                  fontSize: "14px",
                  backgroundColor: brandColors.primary[500],
                  color: "#ffffff",
                }
              }}
              FabProps={{ 
                sx: { 
                  pointerEvents: "auto",
                  border: `1px solid ${brandColors.primary[200]}`,
                  backgroundColor: "#ffffff",
                  "&:hover": {
                    backgroundColor: brandColors.neutral[100]
                  }
                } 
              }}           
            />
          ))}
        </SpeedDial>
      )}

      <Drawer
        anchor="right"
        open={isOpen}
        onClose={handleClose}
        sx={drawerSx}
      >
        <Box sx={drawerHeaderSx}>
          <Typography variant="h6" component="h2">
            Central de Ajuda
          </Typography>
          <IconButton
            onClick={handleClose}
            aria-label="Fechar central de ajuda"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={drawerBodySx}>
          <HelpCenterContent
            categories={categories}
            isLoading={isLoading}
            error={error}
          />
        </Box>
      </Drawer>
    </>
  );
}
